// Where to go after login. NotLoggedInGuard and the /app web client both store the target in
// sessionStorage under TriedAccessing, so it survives the external (Microsoft) login round trip.

export const TRIED_ACCESSING_KEY = "TriedAccessing";
export const WEB_APP_PREFIX = "/app";

/**
 * A same-origin path from a `returnUrl` value, or undefined. Absolute URLs, protocol-relative
 * `//host` and backslash tricks are rejected.
 */
export function sanitizeReturnUrl(value: string | null | undefined, origin: string): string | undefined {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.includes("\\")) return undefined;

    try {
        const url = new URL(trimmed, origin);
        if (url.origin !== origin) return undefined;
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return undefined;
    }
}

/** Reads `?returnUrl=` and remembers it like a guarded route the user tried to open. */
export function rememberReturnUrlFromSearch(search: string, origin: string, storage: Storage): void {
    const returnUrl = sanitizeReturnUrl(new URLSearchParams(search).get("returnUrl"), origin);
    if (returnUrl) storage.setItem(TRIED_ACCESSING_KEY, `${origin}${returnUrl}`);
}

export type LoginRedirect =
    | { kind: "document"; url: string }
    | { kind: "route"; path: string };

/**
 * Where to go once a token exists. Paths of the /app web client need a full page load, since they are
 * a different application; everything else stays inside the router.
 */
export function resolveLoginRedirect(triedAccessing: string | null, origin: string): LoginRedirect {
    if (!triedAccessing || triedAccessing === "undefined") return { kind: "route", path: "/dashboard" };

    let path: string | undefined;
    try {
        const url = new URL(triedAccessing, origin);
        path = url.origin === origin ? `${url.pathname}${url.search}${url.hash}` : undefined;
    } catch {
        path = undefined;
    }

    if (!path) return { kind: "route", path: "/dashboard" };
    if (path === WEB_APP_PREFIX || path.startsWith(`${WEB_APP_PREFIX}/`)) return { kind: "document", url: path };
    return { kind: "route", path };
}

/**
 * DMS-only users (web access, no classic access) go straight to /app once the server enables it.
 * Never while an admin is mimicking a user, so the admin keeps the way back.
 */
export function shouldAutoRedirectToWebApp(
    routing: { autoRedirect: boolean } | null | undefined,
    comingFromUser: string | null,
): boolean {
    return routing?.autoRedirect === true && !comingFromUser;
}
